// Chamar para o trio, ao lado de Adicionar amigo: no perfil, no cartão da mesa, no fim de partida e na aba Amigos.
// Vale para amigo ou não. Bot, você mesmo e quem está bloqueado não têm o botão; convidado que toca é levado a
// guardar a conta com Google. O toque abre "Chamar Léo para o trio" (canvas 7.5c): os seus trios com o estado de
// cada um, "Criar trio novo com Léo" e a linha do plano. A API confere tudo; a folha só mostra.
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ErroDaApi } from './api.ts';
import { type EstadoNoTrio, type FolhaDoTrio, type TrioParaChamar, convidarParaTrio, lerChamarParaTrio } from './amigos.ts';
import { avisarNaTela } from './aviso.tsx';
import { conta, type Sessao } from './conta.ts';
import { sessaoDasRelacoes, useRelacao } from './relacoes.ts';
import { ir } from './rotas.ts';
import { Escudo, VerPlanos } from '../liga/trios.tsx';

export const ROTULO_DO_ESTADO: Record<Exclude<EstadoNoTrio, 'convidar'>, string> = {
  convidado: 'Convite enviado',
  completo: 'Completo',
  ja_esta: 'Já está no trio',
  peca_ao_capitao: 'Peça ao capitão',
};

// "Seu plano Prata permite 3 trios. Você está em 2"
export function linhaDoPlano(p: FolhaDoTrio['plano']): string {
  return `Seu plano ${p.nome} permite ${p.limite} ${p.limite === 1 ? 'trio' : 'trios'}. Você está em ${p.emUso}`;
}

// Escudo com +: o ícone de chamar para o trio.
export function IconeTrioMais() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10 2.5 17 5v6.2c0 4.7-3 8-7 9.8-4-1.8-7-5.1-7-9.8V5z" /><path d="M19.5 14v7M16 17.5h7" />
    </svg>
  );
}

export function BotaoTrio({ usuario, apelido, compacto = false }: { usuario: string; apelido: string; compacto?: boolean }) {
  const estado = useRelacao(usuario);
  const [aberto, setAberto] = useState(false);
  const s = sessaoDasRelacoes();
  // A conta anônima de antes (só na mesa em que estava sentada) não tem o botão.
  if (!s || s.anonimo || !estado || estado === 'indisponivel' || estado === 'bloqueado' || usuario === s.id) return null;
  return (
    <>
      <button className={'chamar-trio' + (compacto ? ' compacto' : '')} aria-label={`Chamar ${apelido} para o trio`}
        onClick={() => setAberto(true)}>
        <IconeTrioMais />{!compacto && <span>Chamar para o trio</span>}
      </button>
      {aberto && <FolhaChamarTrio sessao={s} usuario={usuario} apelido={apelido} aoFechar={() => setAberto(false)} />}
    </>
  );
}

function FolhaChamarTrio({ sessao, usuario, apelido, aoFechar }: { sessao: Sessao; usuario: string; apelido: string; aoFechar: () => void }) {
  const [f, setF] = useState<FolhaDoTrio | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);
  useEffect(() => {
    let vivo = true;
    lerChamarParaTrio(sessao, usuario).then((r) => { if (vivo) setF(r); }, (e: Error) => { if (vivo) setErro(e.message); });
    return () => { vivo = false; };
  }, [usuario]);

  async function convidar(t: TrioParaChamar) {
    if (ocupado) return;
    setOcupado(t.id);
    try {
      await convidarParaTrio(sessao, t.id, usuario);
      setF((x) => x && { ...x, trios: x.trios.map((y) => (y.id === t.id ? { ...y, estado: 'convidado' } : y)) });
      avisarNaTela(`Convite enviado para ${apelido}.`);
    } catch (e) {
      avisarNaTela(e instanceof ErroDaApi ? e.message : 'Não deu certo. Tente de novo.');
    } finally { setOcupado(null); }
  }

  return createPortal(
    <div className="folha-fundo" onClick={(ev) => { if (ev.target === ev.currentTarget) aoFechar(); }}>
      <div className="folha simples folha-chamar-trio" role="dialog" aria-modal="true" aria-labelledby="folha-chamar-trio">
        <h2 id="folha-chamar-trio">Chamar {apelido} para o trio</h2>
        {!f && !erro && <p className="carregando" role="status">Carregando...</p>}
        {erro && <p className="erro" role="alert">{erro}</p>}
        {f && (
          <>
            {f.trios.length > 0 && (
              <ul className="trios-para-chamar">
                {f.trios.map((t) => (
                  <li key={t.id}>
                    <Escudo id={t.escudo} tamanho={36} />
                    <span className="nome"><b>{t.nome}</b><small>{t.membros} de 3</small></span>
                    {t.estado === 'convidar' ? (
                      <button className="btn2 dourado convidar-trio" disabled={ocupado === t.id} aria-label={`Convidar ${apelido} para ${t.nome}`}
                        onClick={() => void convidar(t)}>Convidar</button>
                    ) : (
                      <span className={'estado-trio ' + t.estado}>{ROTULO_DO_ESTADO[t.estado]}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {f.criar === 'pode' ? (
              <button className="btn2 dourado criar-com" onClick={() => { aoFechar(); ir({ tela: 'novo-trio', convidar: usuario }); }}>
                Criar trio novo com {apelido}
              </button>
            ) : (
              <div className="cadeado-tentos">
                {f.criar === 'sem_plano' && <button className="btn2 criar-com bloqueado" disabled>🔒 Criar trio novo com {apelido}</button>}
                {f.criar === 'sem_plano'
                  ? <p className="nota">Criar trio fixo é dos planos Bronze, Prata e Ouro.</p>
                  : <p className="cadeado">{f.mensagemLimite}</p>}
                <VerPlanos />
              </div>
            )}
            <p className="nota plano-trios">{linhaDoPlano(f.plano)}</p>
          </>
        )}
        <button className="btn2" onClick={aoFechar}>Fechar</button>
      </div>
    </div>,
    document.body,
  );
}
